import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { Loader2, CheckCircle2, AlertCircle, MailX } from "lucide-react";
import { Button } from "@/components/ui/button";

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: searchSchema,
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Wypisz się | SyncTech" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type State =
  | { kind: "loading" }
  | { kind: "ready"; email: string }
  | { kind: "already" }
  | { kind: "invalid"; reason: string }
  | { kind: "submitting" }
  | { kind: "done" };

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid", reason: "Brak tokenu w adresie." });
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(
          `/email/unsubscribe?token=${encodeURIComponent(token)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (aborted) return;
        if (res.ok && body?.email) {
          setState({ kind: "ready", email: body.email });
        } else if (body?.already_used || body?.alreadyUnsubscribed) {
          setState({ kind: "already" });
        } else {
          setState({
            kind: "invalid",
            reason: body?.error || "Token jest nieprawidłowy lub wygasł.",
          });
        }
      } catch (err) {
        console.error("[unsubscribe] validate failed", err);
        if (!aborted)
          setState({ kind: "invalid", reason: "Błąd połączenia." });
      }
    })();
    return () => {
      aborted = true;
    };
  }, [token]);

  async function confirm() {
    if (!token) return;
    setState({ kind: "submitting" });
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState({ kind: "done" });
    } catch (err) {
      console.error("[unsubscribe] submit failed", err);
      setState({
        kind: "invalid",
        reason: "Nie udało się przetworzyć żądania. Spróbuj jeszcze raz.",
      });
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-elegant">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <MailX className="h-7 w-7 text-primary" />
        </div>

        {state.kind === "loading" && (
          <Loading message="Sprawdzam link wypisu..." />
        )}

        {state.kind === "ready" && (
          <>
            <h1 className="font-display text-2xl font-semibold">
              Potwierdź wypis
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Czy na pewno chcesz wypisać <strong>{state.email}</strong> z naszej listy mailingowej?
              Nie będziesz już otrzymywać żadnych wiadomości od SyncTech.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Button onClick={confirm} className="flex-1">
                Tak, wypisz mnie
              </Button>
              <Button asChild variant="outline" className="flex-1">
                <Link to="/">Anuluj</Link>
              </Button>
            </div>
          </>
        )}

        {state.kind === "submitting" && (
          <Loading message="Przetwarzam żądanie..." />
        )}

        {state.kind === "done" && (
          <>
            <Status
              icon={<CheckCircle2 className="h-5 w-5" />}
              tone="success"
              title="Zostałeś wypisany"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              Twój adres został usunięty z listy. Nie będziemy już wysyłać Ci wiadomości.
              Jeśli zmienisz zdanie, napisz na <a href="mailto:kontakt@synctech.pl" className="text-primary underline">kontakt@synctech.pl</a>.
            </p>
            <Button asChild className="mt-6 w-full">
              <Link to="/">Wróć na stronę główną</Link>
            </Button>
          </>
        )}

        {state.kind === "already" && (
          <>
            <Status
              icon={<CheckCircle2 className="h-5 w-5" />}
              tone="muted"
              title="Już wypisany"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              Ten adres został już wcześniej wypisany. Nie musisz nic robić.
            </p>
            <Button asChild className="mt-6 w-full" variant="outline">
              <Link to="/">Wróć na stronę główną</Link>
            </Button>
          </>
        )}

        {state.kind === "invalid" && (
          <>
            <Status
              icon={<AlertCircle className="h-5 w-5" />}
              tone="destructive"
              title="Nieprawidłowy link"
            />
            <p className="mt-3 text-sm text-muted-foreground">{state.reason}</p>
            <Button asChild className="mt-6 w-full" variant="outline">
              <Link to="/">Wróć na stronę główną</Link>
            </Button>
          </>
        )}
      </div>
    </main>
  );
}

function Loading({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {message}
    </div>
  );
}

function Status({
  icon,
  tone,
  title,
}: {
  icon: React.ReactNode;
  tone: "success" | "destructive" | "muted";
  title: string;
}) {
  const colorClass =
    tone === "success"
      ? "text-primary"
      : tone === "destructive"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className={`flex items-center gap-2 font-display text-xl font-semibold ${colorClass}`}>
      {icon}
      {title}
    </div>
  );
}
